import { stringify } from "qs";

import { AnalyticsData, AnalyticsPlugin } from "../Analytics";
import { AnalyticsEvent } from "../events";

function scriptLoaded(src: any) {
    const scripts = document.querySelectorAll("script[src]");
    return Object.keys(scripts).filter(key => (scripts as any)[key].src === src).length > 0;
}

/** The events the GTM container read before `sol` existed: their properties stay at the root, where it expects them */
const ROOT_PROPERTY_EVENTS = new Set<AnalyticsEvent>([
    AnalyticsEvent.PagePrint,
    AnalyticsEvent.UserIdentified,
    AnalyticsEvent.OptOut,
    AnalyticsEvent.OptIn,
    AnalyticsEvent.LoginSuccess,
    AnalyticsEvent.LoginFailure,
]);

class GoogleTagManagerPlugin implements AnalyticsPlugin {
    id: string;
    dataLayer: any;

    constructor(config: any) {
        this.id = config.id;
        this.dataLayer = [];

        const scriptSrc = `https://www.googletagmanager.com/gtm.js?${stringify(config)}`;

        if (!scriptLoaded(scriptSrc)) {
            const dataLayerName = config.l || "dataLayer";
            window[dataLayerName] = window[dataLayerName] || [];
            this.dataLayer = window[dataLayerName];
            this.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
            this.dataLayer.push({ event: AnalyticsEvent.OptIn, cookieConsent: true });

            const script = document.createElement("script");
            script.async = true;
            script.src = scriptSrc;

            const body = document.getElementsByTagName("body")[0];
            if (body.firstChild) {
                body.insertBefore(script, body.firstChild);
            } else {
                body.appendChild(script);
            }
        }
    }

    gtm() {
        if (typeof window !== "undefined") {
            const gtm = (window as any)["google_tag_manager"];
            return gtm && gtm[this.id];
        }

        return null;
    }

    /**
     * GTM merges every push into one data model that outlives the event. The properties go under `sol`, read in GTM
     * as `sol.<property>`, so they stay clear of other tags' keys. Clearing `sol` first keeps an event from inheriting
     * the previous event's properties, as Google advises for `ecommerce`.
     */
    track(event: AnalyticsEvent, properties: AnalyticsData, infos: AnalyticsData) {
        if (ROOT_PROPERTY_EVENTS.has(event)) {
            this.dataLayer.push({ event, ...infos, ...properties });
        } else {
            this.dataLayer.push({ sol: null });
            this.dataLayer.push({ event, ...infos, sol: properties });
        }
    }

    reset() {
        const dataLayer = this.gtm()?.dataLayer;
        if (dataLayer) {
            dataLayer.reset();
        }
    }
}

export default GoogleTagManagerPlugin;
